import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Rocket, Users, X, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function CreatePackDialog({ open, onClose, onCreated }) {
  const { user, token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [users, setUsers] = useState([]);              // full active user list
  const [creating, setCreating] = useState(false);
  const [assignedUserIds, setAssignedUserIds] = useState([]);  // preselected below
  const [assignedFilter, setAssignedFilter] = useState('');
  const [showAssignedList, setShowAssignedList] = useState(false);
  const [form, setForm] = useState({
    client_name: '',
    broker: '',
    lender: '',
    template_id: '',
    credit_team_member: '',
    credit_team_member_id: '',
    application_number: '',
    deal_number: '',
  });

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!open) return;
    fetch(`${backendUrl}/api/lender-packs/templates`, { headers: auth })
      .then(r => r.json())
      .then(d => setTemplates(d.templates || []))
      .catch(() => setTemplates([]));
    fetch(`${backendUrl}/api/gameplan/options/brokers`, { headers: auth })
      .then(r => r.json())
      .then(d => setBrokers(Array.isArray(d) ? d : (d.brokers || [])))
      .catch(() => setBrokers([]));
    // Fetch active users for credit officer picker + assigned users list
    fetch(`${backendUrl}/api/lender-packs/users`, { headers: auth })
      .then(r => r.json())
      .then(d => {
        const list = d.users || [];
        setUsers(list);
        // Preselect ALL active users by default per product spec
        setAssignedUserIds(list.map(u => u.id));
      })
      .catch(() => setUsers([]));
    // Default broker to the logged-in user
    setForm(f => ({ ...f, broker: user?.name || f.broker }));
  }, [open, auth, user?.name]);

  const toggleUser = (uid) => {
    setAssignedUserIds(list => list.includes(uid) ? list.filter(x => x !== uid) : [...list, uid]);
  };

  const removeUser = (uid) => setAssignedUserIds(list => list.filter(x => x !== uid));

  const selectAll = () => setAssignedUserIds(users.map(u => u.id));
  const selectNone = () => setAssignedUserIds([]);

  const filteredUsers = useMemo(() => {
    const q = assignedFilter.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q));
  }, [users, assignedFilter]);

  const handleSubmit = async () => {
    if (!form.client_name || !form.broker || !form.template_id) {
      toast.error('Client, broker and template are required');
      return;
    }
    if (assignedUserIds.length === 0) {
      const proceed = confirm('No users are assigned to this pack. Only administrators will be able to see it. Continue?');
      if (!proceed) return;
    }
    setCreating(true);
    const chosen = templates.find(t => t.id === form.template_id);
    // Look up credit officer name from the picked user id (or keep freeform if none picked)
    const creditUser = users.find(u => u.id === form.credit_team_member_id);
    const creditName = creditUser?.name || form.credit_team_member || '';
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({
          ...form,
          credit_team_member: creditName,
          lender: chosen?.lender || form.lender,
          assigned_user_ids: assignedUserIds,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const created = await res.json();
      toast.success(`Pack created — ${created.lender}`);
      onCreated?.(created);
      onClose();
    } catch (e) {
      toast.error(`Failed to create pack — ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const assignedUsers = assignedUserIds.map(id => users.find(u => u.id === id)).filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="create-pack-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Rocket className="w-4 h-4 text-blue-600" />
            New Lender Submission Pack
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Pick a client, broker, lender template and the team members who should have access to this pack.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">Client name *</label>
            <Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })}
              placeholder="e.g. John & Jane Smith" data-testid="pack-client-name" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Broker *</label>
              <Select value={form.broker} onValueChange={v => setForm({ ...form, broker: v })}>
                <SelectTrigger data-testid="pack-broker"><SelectValue placeholder="Select broker" /></SelectTrigger>
                <SelectContent>
                  {brokers.map(b => (
                    <SelectItem key={b.id || b.name} value={b.name}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Lender / Template *</label>
              <Select value={form.template_id} onValueChange={v => setForm({ ...form, template_id: v })}>
                <SelectTrigger data-testid="pack-template"><SelectValue placeholder="Select template" /></SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.lender}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">Credit officer (optional)</label>
            <Select
              value={form.credit_team_member_id || '__none__'}
              onValueChange={v => {
                if (v === '__none__') { setForm({ ...form, credit_team_member_id: '', credit_team_member: '' }); return; }
                const u = users.find(u => u.id === v);
                setForm({ ...form, credit_team_member_id: v, credit_team_member: u?.name || '' });
              }}
            >
              <SelectTrigger data-testid="pack-credit-team-select">
                <SelectValue placeholder="Select an existing user account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No credit officer assigned —</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} <span className="opacity-60 ml-1">· {u.role}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10.5px] text-slate-500 mt-1">Choose from active user accounts. Manage users in Integrations Command Centre.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Application #</label>
              <Input value={form.application_number}
                onChange={e => setForm({ ...form, application_number: e.target.value })}
                placeholder="e.g. APP-2026-001" data-testid="pack-app-no" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Deal #</label>
              <Input value={form.deal_number}
                onChange={e => setForm({ ...form, deal_number: e.target.value })}
                placeholder="e.g. D-42" data-testid="pack-deal-no" />
            </div>
          </div>

          {/* Assigned Users */}
          <div className="rounded-xl border border-slate-200 p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-slate-700" />
                <span className="text-xs font-black uppercase tracking-widest text-slate-700">Assigned Users</span>
                <span className="text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full bg-slate-800 text-white">{assignedUsers.length}/{users.length}</span>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={selectAll} className="text-[10.5px] font-bold text-blue-600 hover:underline" data-testid="assigned-select-all">Select all</button>
                <span className="text-slate-300">·</span>
                <button type="button" onClick={selectNone} className="text-[10.5px] font-bold text-rose-600 hover:underline" data-testid="assigned-select-none">Clear</button>
              </div>
            </div>

            <p className="text-[10.5px] text-slate-500 mb-2">
              All active users are preselected by default. Remove anyone who should not have access. Selected users can view and edit this pack.
            </p>

            {/* Chips */}
            {assignedUsers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="assigned-users-chips">
                {assignedUsers.map(u => (
                  <span key={u.id} className="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-800 border border-blue-200">
                    <span className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center text-white text-[9px] font-black">
                      {(u.name || '?').charAt(0).toUpperCase()}
                    </span>
                    <span>{u.name}</span>
                    <button type="button" onClick={() => removeUser(u.id)}
                      className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-blue-200"
                      title={`Remove ${u.name}`}
                      data-testid={`remove-assigned-${u.id}`}>
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowAssignedList(v => !v)}
              className="w-full text-left text-[11px] font-bold text-slate-700 flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-slate-100"
              data-testid="assigned-toggle-list"
            >
              <span>{showAssignedList ? 'Hide user list' : 'Add / edit users…'}</span>
              {showAssignedList ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>

            {showAssignedList && (
              <div className="mt-2" data-testid="assigned-user-picker">
                <div className="relative mb-2">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input value={assignedFilter} onChange={e => setAssignedFilter(e.target.value)}
                    placeholder="Filter by name, email or role…"
                    className="pl-7 h-8 text-xs" data-testid="assigned-filter" />
                </div>
                <div className="max-h-60 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
                  {filteredUsers.length === 0 ? (
                    <div className="text-center py-4 text-[11px] italic text-slate-500">No users match.</div>
                  ) : filteredUsers.map(u => {
                    const checked = assignedUserIds.includes(u.id);
                    return (
                      <label key={u.id} className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-slate-50 ${checked ? 'bg-blue-50' : ''}`} data-testid={`assigned-row-${u.id}`}>
                        <Checkbox checked={checked} onCheckedChange={() => toggleUser(u.id)} />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-slate-900 truncate">{u.name}</div>
                          <div className="text-[10px] text-slate-500 truncate">{u.email} · {u.role}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={creating} data-testid="pack-create-submit"
            className="bg-slate-900 hover:bg-slate-800 text-white">
            {creating ? 'Creating…' : 'Create pack'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
